import { Box, Typography } from "@mui/material"
import type { FC } from "react"
import React from "react"

import { FilterChip } from "@/components/common/Filters"
import useProductManager from "@/hooks/api/useProductManager"
import { PRICE_CHANGE_SUBMISSION_STATUS_LABELS } from "@/screens/Pricing/constants"
import type { PriceChangeHistorySearchParams } from "@/screens/Pricing/types/searchParams"
import { formatDate } from "@/utils/date"

interface PriceChangeHistoryFilterChipsProps {
  onClearFilterChip: (key: keyof PriceChangeHistorySearchParams, value?: string) => void
  searchParams: PriceChangeHistorySearchParams
}

const PriceChangeHistoryFilterChips: FC<PriceChangeHistoryFilterChipsProps> = ({ onClearFilterChip, searchParams }) => {
  const { assignedToIds = [], dateTo, dateFrom, status } = searchParams

  const { productManagerList } = useProductManager({
    query: "",
    isAs400ProductManager: true,
    userIds: assignedToIds,
    pause: !assignedToIds.length,
  })

  if (!assignedToIds.length && !dateTo && !dateFrom && !status?.length) {
    return null
  }

  return (
    <Box className='flex flex-wrap items-center gap-2' data-testid='selling-price-history.filter-chips'>
      <Typography variant='body1' className='text-gray-700'>
        Applied filters:
      </Typography>
      {Boolean(assignedToIds?.length) &&
        productManagerList.map((productManager) => (
          <FilterChip
            key={productManager.userId}
            label={`Assigned To: ${productManager.name}`}
            onDelete={() => onClearFilterChip("assignedToIds", productManager.userId)}
            testId={`selling-price-history.filter-chip.assigned-to-${productManager.userId}`}
          />
        ))}

      {dateFrom && (
        <FilterChip
          label={`Date From: ${formatDate(dateFrom)}`}
          onDelete={() => onClearFilterChip("dateFrom")}
          testId='selling-price-history.filter-chips.date-from'
        />
      )}

      {dateTo && (
        <FilterChip
          label={`Date To: ${formatDate(dateTo)}`}
          onDelete={() => onClearFilterChip("dateTo")}
          testId='selling-price-history.filter-chips.date-to'
        />
      )}

      {Boolean(status?.length) &&
        status?.map((value) => {
          const label = PRICE_CHANGE_SUBMISSION_STATUS_LABELS[value]
          if (!label) return null
          return (
            <FilterChip
              key={value}
              label={`Status: ${label}`}
              onDelete={() => onClearFilterChip("status", value)}
              testId={`selling-price-history.filter-chips.status-${value}`}
            />
          )
        })}
    </Box>
  )
}
export default PriceChangeHistoryFilterChips
